import {
  ApiPermissions,
  Fee,
  MarketPair,
  MarketRate,
  OrderType,
  OrderBookMetrics,
  SelectedAccount,
  TradeForm,
  TradePathData,
  WidgetConfig,
} from "@cede/types";
import { DroplistAccounts } from "@/hooks/accounts/types";
import { ITradingUneligibleData } from "@/hooks/trading/config/useTradeWidgetConfig";

export type DropListWithIconItem<T = Record<string, unknown>> = {
  label: string;
  value: string;
  img: string;
  type?: string;
  searchableTerms?: string[];
  defaultImg?: string;
} & T;

type Tab = {
  value: string;
  label: string;
  disabled: boolean;
};

export type UseTradeState = {
  orderType: OrderType;
  selectOrderType: (tab: OrderType) => void;
  tradeAbleAsset: Record<string, DropListWithIconItem[]>;
  availableAccounts: DroplistAccounts;
  notConnectedAccounts: DroplistAccounts;
  isSubmitButtonActive: boolean;
  switchToken: () => void;
  tabs: Tab[];
  portfolioAssets: DropListWithIconItem[];
  assetBalance: string | number | null;
  submitTrade: () => Promise<void>;
  tokenFromPrice: number;
  tokenToPrice: number;
  marketRate: MarketRate;
  tradeForm: TradeForm;
  dispatchForm: (action: TradeFormAction<TradeForm>) => void;
  minAmountFrom: string | undefined;
  maxAmountFrom: string | undefined;
  amountToIsLoading: boolean;
  warningMessage: string;
  isSwitchIconHovered: boolean;
  setIsSwitchIconHovered: (isSwitchIconHovered: boolean) => void;
  assetToLoading: boolean;
  cexFeeIsLoading: boolean;
  setFocusedInput: (value: "amountFrom" | "amountTo" | null) => void;
  fee: Fee | undefined;
  disabledSwitchMessage: string;
  isSwitchable: boolean;
  addCexRedirect?: VoidFunction;
  addMax: () => void;
  tradeError: TradeErrorType | undefined;
  isLoadingBalances: boolean;
  isLoadingUnderlyingMarkets: boolean;
  shouldDisableCex: (v: DropListWithIconItem<{ cexValue?: string; permissions?: ApiPermissions[] }>) => boolean;
  allowUserInputAmountTo: boolean;
  tradePath: TradePathData[] | null | undefined;
  quoteToken: DropListWithIconItem | null;
  price: string;
  slippage: number;
  isLimitOrderSupported: boolean | undefined;
  config: WidgetConfig;
  connectExchangeAccount: () => void;
  connectSpecificExchangeAccount: (exchangeId: string) => void;
  isServerSide?: boolean;
  isMultipleTransactionsTrade: boolean | undefined;
  orderBookMetrics: OrderBookMetrics | null | undefined;
  amountFromIsLoading: boolean;
  isAccountTransitioning: boolean;
};

export type AvailableAsset = DropListWithIconItem<{ balance: string; freeBalance: number; amount: string }>;

export type AvailableAssets = AvailableAsset[];

export type UseTradeApiState = {
  assetsFrom: AvailableAssets;
  assetsTo: AvailableAssets;
  assetsToAreLoading: boolean;
  underlyingMarkets: MarketPair[];
  marketRate: MarketRate;
  isLoadingBalances: boolean;
  isLoadingUnderlyingMarkets: boolean;
  shouldUseTradeSquare: boolean;
  tradePath: TradePathData[] | null | undefined;
  droplistDestinationTokensError: Error | null;
  droplistDestinationTokensAreLoading: boolean;
  markets: MarketPair[];
  isLimitOrderSupported: boolean | undefined;
  orderBookMetrics: OrderBookMetrics | null | undefined;
  tradePathBySupportedTokens: Record<string, TradePathData[]> | null | undefined;
};

export type UseTradeOpts = {
  addCex?: () => void;
  tradingUneligibleData?: ITradingUneligibleData;
};

export type UseTradeHook = (opts?: UseTradeOpts) => UseTradeState;
export type UseTradeApiHook = (params: {
  selectedAccount?: SelectedAccount;
  dispatchForm: (action: TradeFormAction<TradeForm>) => void;
  tokenFrom: DropListWithIconItem | null;
  form: TradeForm | null;
}) => UseTradeApiState;

export type PresentationalTradeProps = {
  controller: UseTradeState;
};

export type TradeFormAction<T> = {
  field: keyof T | "revertToken";
  value?: T[keyof T];
};

export enum TradeErrorType {
  INSUFFICIENT_BALANCE_FOR_MIN = "INSUFFICIENT_BALANCE_FOR_MIN",
  NO_TOKENS_AVAILABLE = "NO_TOKENS_AVAILABLE",
  AMOUNT_EXCEEDS_BALANCE = "AMOUNT_EXCEEDS_BALANCE",
  AMOUNT_BELOW_MINIMUM = "AMOUNT_BELOW_MINIMUM",
  AMOUNT_ABOVE_MAXIMUM = "AMOUNT_ABOVE_MAXIMUM",
  HIGH_SLIPPAGE = "HIGH_SLIPPAGE",
}
